import type * as React from "react";

/**
 * Decorative deliverable preview stack for the homepage hero.
 * Pure markup + CSS mock-ups of real deliverables (AI opportunity map,
 * opportunity score) — no photography, no image files.
 */
export function DeliverablePreview() {
  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-[560px] select-none">
      {/* spacer keeps the absolutely-positioned cards in flow */}
      <div className="pb-[86%] sm:pb-[78%]" />

      {/* Back card — AI opportunity map */}
      <div
        className="absolute left-0 top-0 hidden w-[70%] rounded-xl border border-ink-200 bg-paper-0 p-4 shadow-[0_2px_8px_oklch(0.1831_0.0309_263.38_/_0.06),0_24px_64px_oklch(0.1831_0.0309_263.38_/_0.14)] sm:block"
        style={{ transform: "rotate(-3deg)" }}
      >
        <div className="flex items-center gap-2 border-b border-ink-100 pb-3">
          <span className="h-2 w-2 rounded-full bg-ink-200" />
          <span className="h-2 w-2 rounded-full bg-ink-200" />
          <span className="h-2 w-2 rounded-full bg-ink-200" />
          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">
            AI Opportunity Map
          </span>
        </div>

        <div className="mt-4 h-3 w-[62%] rounded-sm bg-ink-800" />
        <div className="mt-3 space-y-2">
          <div className="h-2 w-[92%] rounded-full bg-ink-100" />
          <div className="h-2 w-[78%] rounded-full bg-ink-100" />
          <div className="h-2 w-[58%] rounded-full bg-ink-100" />
          <div className="h-2 w-[34%] rounded-full bg-brand-300" />
        </div>

        <div className="mt-5 flex h-20 items-end gap-2">
          <div className="laa-col h-[34%] w-full rounded-sm bg-brand-200" />
          <div className="laa-col h-[50%] w-full rounded-sm bg-brand-300" />
          <div className="laa-col h-[64%] w-full rounded-sm bg-brand-400" />
          <div className="laa-col h-[82%] w-full rounded-sm bg-brand-500" />
          <div className="laa-col h-full w-full rounded-sm bg-brand-600" />
        </div>
      </div>

      {/* Front card — opportunity score */}
      <div
        className="absolute bottom-0 left-1/2 w-[86%] -translate-x-1/2 rounded-xl border border-ink-200 bg-paper-0 p-4 shadow-[0_2px_8px_oklch(0.1831_0.0309_263.38_/_0.06),0_24px_64px_oklch(0.1831_0.0309_263.38_/_0.14)] sm:left-auto sm:right-0 sm:w-[64%] sm:translate-x-0"
        style={{ transform: "rotate(2.4deg)" }}
      >
        <div className="border-b border-ink-100 pb-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500">
            Opportunity Score
          </span>
        </div>

        <div className="mt-4 flex justify-center">
          <div className="relative flex size-24 items-center justify-center">
            <svg viewBox="0 0 104 104" className="absolute inset-0 size-full -rotate-90">
              <circle
                cx="52"
                cy="52"
                r="47"
                fill="none"
                stroke="var(--color-ink-200)"
                strokeWidth="8"
              />
              <circle
                className="laa-ringpath"
                cx="52"
                cy="52"
                r="47"
                fill="none"
                stroke="var(--color-brand-600)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray="295"
                style={{ "--laa-ring-end": 65 } as React.CSSProperties}
              />
            </svg>
            <span className="tabular relative font-display text-h3 font-semibold text-ink-900">
              78
            </span>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {[
            { label: "Process fit", value: 84, tone: "bg-brand-600", delay: "0ms" },
            { label: "Data readiness", value: 71, tone: "bg-brand-600", delay: "120ms" },
            { label: "Team capacity", value: 52, tone: "bg-accent-500", delay: "240ms" },
          ].map((row) => (
            <div key={row.label}>
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-ink-500">
                <span>{row.label}</span>
                <span className="tabular text-ink-900">{row.value}</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-ink-100">
                <div
                  className={`laa-bar h-full rounded-full ${row.tone}`}
                  style={{ width: `${row.value}%`, animationDelay: row.delay }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DeliverablePreview;
